import is from '@sindresorhus/is';
import { useQuery } from '@tanstack/react-query';

import type {
    CompanyInformationErrorCode,
    CompanyInformationGenerationResult,
} from '../../data-contract/company-information-generation';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

const POLL_INTERVAL_MS = 2000;

export type UseCompanyInformationGenerationResult = {
    readonly errorCode: CompanyInformationErrorCode | null;
    readonly generatedForCompanyDomain: string | null;
    readonly generatedForCompanyName: string | null;
    readonly hasQueryError: boolean;
    readonly isGenerating: boolean;
    readonly isReady: boolean;
    readonly result: CompanyInformationGenerationResult | null;
};

export const useCompanyInformationGeneration = (
    jobArchitectureId: string | undefined,
    options?: { readonly enabled?: boolean },
): UseCompanyInformationGenerationResult => {
    const { fetchCompanyInformationGenerationStatus } = useApplicationFetchers();

    const query = useQuery({
        enabled: !is.undefined(jobArchitectureId) && (options?.enabled ?? true),
        queryFn: async ({ signal }) => {
            const result = await fetchCompanyInformationGenerationStatus({
                jobArchitectureId: jobArchitectureId!,
                signal,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: jobArchitectureKeys.companyInformationGeneration(jobArchitectureId!),
        refetchInterval: (q) => {
            return q.state.data?.status === 'PENDING' ? POLL_INTERVAL_MS : false;
        },
        refetchOnWindowFocus: false,
        retry: false,
        staleTime: 0,
        throwOnError: false,
    });

    const data = query.data;

    return {
        errorCode: data?.errorCode ?? null,
        generatedForCompanyDomain: data?.companyDomain ?? null,
        generatedForCompanyName: data?.companyName ?? null,
        hasQueryError: query.isError,
        isGenerating: data?.status === 'PENDING',
        isReady: query.isSuccess,
        result: data?.result ?? null,
    };
};
